import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { workedHoursAPI } from '../../../services/apiServices';
import { useFeedback } from '../../../contexts/FeedbackContext';
import { Alert, Input, Modal, Select } from '../../../components/common/UI';
import DateField from '../../../components/common/DateField';
import { EditIcon, PlusIcon, TrashIcon } from '../../../components/common/Icons';
import MoneyValue from '../../../components/common/MoneyValue';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { formatLocalizedDate, toDateInputValue, todayInputValue } from '../../../utils/dateUtils';
import { formatDecimalInput, formatDecimalValue, formatMoneyNumber, parseDecimalInput } from '../../../utils/currency';
import { requiredFieldMessage, requiredFieldsMessage } from '../../../utils/validationMessages';

const createEmptyForm = () => ({
  id: null,
  serviceName: '',
  workerName: '',
  responsibleInvolvedId: '',
  date: todayInputValue(),
  time: '',
  hourlyRate: '',
  totalHours: '',
});

const getInvolvedName = (item) => item?.client?.name || item?.involvedName || '';
const getResponsibleName = (item) => getInvolvedName(item.responsibleInvolved) || item.workerName || item.responsibleName || '';
const getHourRate = (item) => Number(item.hourlyRate || item.rate || 0);
const getHourAmount = (item) => Number(item.totalHours || item.hours || 0);
const recordTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};
const sortByCreatedDesc = (items = []) => (
  [...items].sort((a, b) => recordTime(b.createdAt || b.updatedAt || b.date) - recordTime(a.createdAt || a.updatedAt || a.date))
);
const isPaidHour = (item) => getHourRate(item) > 0;
const isFutureWorkedHour = (date, time) => {
  if (!date || !time) return false;
  const entryDate = new Date(`${date}T${String(time).slice(0, 5)}:00`);
  return !Number.isNaN(entryDate.getTime()) && entryDate.getTime() > Date.now();
};
const currentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const WorkedHoursTab = ({ businessId, data = [], involved = [], onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [detail, setDetail] = useState(null);
  const { confirm, notify } = useFeedback();

  const rows = useMemo(() => sortByCreatedDesc(data), [data]);
  const totalHours = rows.reduce((sum, item) => sum + getHourAmount(item), 0);
  const paidHours = rows.filter(isPaidHour).reduce((sum, item) => sum + getHourAmount(item), 0);
  const freeHours = rows.filter((item) => !isPaidHour(item)).reduce((sum, item) => sum + getHourAmount(item), 0);
  const totalValue = rows.reduce((sum, item) => sum + getHourRate(item) * getHourAmount(item), 0);
  const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : 'pt-BR';
  const involvedOptions = useMemo(() => [
    { value: '', label: t('progress.noResponsibleInvolved') },
    ...involved.map((person) => ({ value: person.id, label: getInvolvedName(person) || t('involved.manualPerson') })),
  ], [involved, t]);

  const openCreate = () => {
    setForm(createEmptyForm());
    setFieldErrors({});
    setError('');
    setModal(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      serviceName: item.serviceName || '',
      workerName: item.workerName || '',
      responsibleInvolvedId: item.responsibleInvolvedId || item.responsibleInvolved?.id || '',
      date: toDateInputValue(item.date),
      time: item.time?.slice(0, 5) || '',
      hourlyRate: formatDecimalValue(item.hourlyRate, i18n.language),
      totalHours: formatDecimalValue(item.totalHours, i18n.language),
    });
    setFieldErrors({});
    setError('');
    setModal(true);
  };

  const handleSave = async () => {
    const hourlyRate = form.hourlyRate === '' ? null : parseDecimalInput(form.hourlyRate);
    const totalHoursValue = parseDecimalInput(form.totalHours);
    const errors = {};
    const missingLabels = [];
    if (!form.serviceName.trim()) {
      errors.serviceName = requiredFieldMessage(t, t('hours.serviceLabel'));
      missingLabels.push(t('hours.serviceLabel'));
    }
    if (!form.date) {
      errors.date = requiredFieldMessage(t, t('common.dateRequired'));
      missingLabels.push(t('common.date'));
    }
    if (String(form.totalHours).trim() === '') {
      errors.totalHours = requiredFieldMessage(t, t('hours.totalHoursLabel'));
      missingLabels.push(t('hours.totalHoursLabel'));
    } else if (totalHoursValue <= 0) {
      errors.totalHours = t('hours.totalRequired');
    }
    if (form.time && !form.time.match(/^([01]\d|2[0-3]):[0-5]\d$/)) errors.time = t('hours.timeRequired');
    if (hourlyRate !== null && (Number.isNaN(hourlyRate) || hourlyRate < 0)) errors.hourlyRate = t('hours.rateRequired');
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError(missingLabels.length ? requiredFieldsMessage(t, missingLabels) : Object.values(errors)[0]);
      return;
    }
    setFieldErrors({});
    if (isFutureWorkedHour(form.date, form.time)) {
      setError(t('hours.futureDateError'));
      return;
    }

    setSaving(true);
    setError('');
    const payload = {
      serviceName: form.serviceName,
      workerName: form.workerName.trim() || null,
      responsibleInvolvedId: form.responsibleInvolvedId || null,
      date: form.date,
      time: form.time || null,
      hourlyRate,
      totalHours: totalHoursValue,
    };

    try {
      if (form.id) await workedHoursAPI.update(businessId, form.id, payload);
      else await workedHoursAPI.create(businessId, payload);
      setModal(false);
      setForm(createEmptyForm());
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err, t('hours.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const deleteDescription = (item) => {
    const date = formatLocalizedDate(item.date, locale) || t('common.date');
    const hours = formatMoneyNumber(getHourAmount(item), { language: i18n.language });
    const rateLabel = isPaidHour(item) ? t('hours.paid') : t('hours.integratedFree');
    return t('hours.deleteDetailMessage', {
      detail: `${item.serviceName || t('hours.entries')} - ${date} - ${hours}h - ${rateLabel}`,
    });
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      variant: 'danger',
      title: t('hours.deleteTitle'),
      message: deleteDescription(item),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await workedHoursAPI.remove(businessId, item.id);
      onRefresh();
    } catch {
      notify({ variant: 'danger', title: t('hours.deleteErrorTitle'), message: t('hours.deleteError') });
    }
  };

  const calculatedTotal =
    (parseDecimalInput(form.hourlyRate) || 0) *
    (parseDecimalInput(form.totalHours) || 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <Metric label={t('reports.totalHours')} value={`${totalHours.toFixed(0)}h`} />
        <Metric label={t('reports.totalValue')} value={<MoneyValue value={totalValue} />} className="text-sky-500" />
        <Metric label={t('hours.paidHours')} value={`${paidHours.toFixed(0)}h`} className="text-emerald-600" />
        <Metric label={t('hours.freeHours')} value={`${freeHours.toFixed(0)}h`} className="text-amber-600" />
      </div>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
        <div className="flex min-h-16 items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
          <h2 className="font-bold text-slate-900">{t('hours.entries')}</h2>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-500 px-5 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600" onClick={openCreate} type="button">
            <PlusIcon className="h-4 w-4" />
            {t('common.new')}
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500">{t('hours.empty')}</p>
        ) : (
          <div className="divide-y divide-slate-200">
            {rows.map((item) => {
              const itemTotal = getHourRate(item) * getHourAmount(item);
              const paid = isPaidHour(item);
              return (
                <div key={item.id} className="flex min-h-[84px] cursor-pointer items-center gap-4 px-4 py-3 transition hover:bg-slate-50" onClick={() => setDetail(item)} role="button" tabIndex={0}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-slate-900">{item.serviceName}</p>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span>{formatLocalizedDate(item.date, locale)}</span>
                      {item.time && <span>{t('hours.startTime')}: {item.time}</span>}
                      <span>{t('hours.totalHoursLabel')}: {formatMoneyNumber(getHourAmount(item), { language: i18n.language })}h</span>
                      {getResponsibleName(item) && <span>{getResponsibleName(item)}</span>}
                      {paid ? <span><MoneyValue value={item.hourlyRate} />/h</span> : <span className="font-semibold text-amber-600">{t('hours.integratedFree')}</span>}
                    </div>
                  </div>
                  <p className={`font-bold ${paid ? 'text-sky-500' : 'text-amber-600'}`}>{paid ? <MoneyValue value={itemTotal} /> : t('hours.free')}</p>
                  <div className="flex gap-1">
                    <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100" onClick={(event) => { event.stopPropagation(); openEdit(item); }} type="button" aria-label={t('hours.edit')}>
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" onClick={(event) => { event.stopPropagation(); handleDelete(item); }} type="button" aria-label={t('hours.delete')}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={form.id ? t('hours.edit') : t('hours.register')} size="md">
        {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Input className="md:col-span-2" label={t('hours.serviceLabel')} value={form.serviceName} onChange={(event) => { setForm({ ...form, serviceName: event.target.value }); setFieldErrors((current) => ({ ...current, serviceName: '' })); }} placeholder={t('hours.servicePlaceholder')} error={fieldErrors.serviceName} />
          <Select label={t('progress.responsibleInvolved')} value={form.responsibleInvolvedId} onChange={(event) => setForm({ ...form, responsibleInvolvedId: event.target.value })} options={involvedOptions} />
          <Input label={t('hours.responsibleLabel')} value={form.workerName} onChange={(event) => setForm({ ...form, workerName: event.target.value })} placeholder={t('hours.responsiblePlaceholder')} />
          <DateField label={t('common.dateRequired')} value={form.date} onChange={(date) => { setForm({ ...form, date }); setFieldErrors((current) => ({ ...current, date: '' })); }} helpText={t('common.dateHelp')} max={todayInputValue()} error={fieldErrors.date} />
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-slate-900">{t('hours.timeLabel')}</span>
              <button className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700" onClick={() => setForm({ ...form, time: '' })} type="button">
                {t('hours.noTime')}
              </button>
            </div>
            <div className="flex gap-2">
              <Input className="flex-1" type="time" value={form.time} onChange={(event) => { setForm({ ...form, time: event.target.value }); setFieldErrors((current) => ({ ...current, time: '' })); }} error={fieldErrors.time} />
              <button className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900" onClick={() => setForm({ ...form, time: currentTime() })} type="button">
                {t('common.now')}
              </button>
            </div>
          </div>
          <Input label={t('hours.hourlyRateLabel')} inputMode="decimal" value={form.hourlyRate} onChange={(event) => { setForm({ ...form, hourlyRate: formatDecimalInput(event.target.value, i18n.language) }); setFieldErrors((current) => ({ ...current, hourlyRate: '' })); }} placeholder={t('hours.hourlyRatePlaceholder')} helpText={t('hours.hourlyRateOptional')} error={fieldErrors.hourlyRate} />
          <Input label={t('hours.totalHoursLabel')} inputMode="decimal" value={form.totalHours} onChange={(event) => { setForm({ ...form, totalHours: formatDecimalInput(event.target.value, i18n.language) }); setFieldErrors((current) => ({ ...current, totalHours: '' })); }} placeholder={t('hours.totalHoursPlaceholder')} error={fieldErrors.totalHours} />
        </div>
        <div className="mt-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-sky-700">{t('hours.calculatedTotal')}</p>
          {parseDecimalInput(form.hourlyRate) > 0 ? (
            <p className="mt-1 text-xl font-extrabold text-slate-900"><MoneyValue value={calculatedTotal} /></p>
          ) : (
            <p className="mt-1 text-lg font-extrabold text-amber-700">{t('hours.integratedFree')}</p>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <button className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white disabled:opacity-60" onClick={handleSave} disabled={saving} type="button">
            {saving ? t('common.saving') : t('common.save')}
          </button>
          <button className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900" onClick={() => setModal(false)} type="button">
            {t('common.cancel')}
          </button>
        </div>
      </Modal>
      <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.serviceName || t('hours.entries')} size="sm">
        {detail && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Info label={t('hours.serviceName')} value={detail.serviceName || '-'} />
            <Info label={t('progress.responsibleName')} value={getResponsibleName(detail) || '-'} />
            <Info label={t('common.date')} value={formatLocalizedDate(detail.date, locale) || '-'} />
            <Info label={t('hours.startTime')} value={detail.time || '-'} />
            <Info label={t('hours.totalHoursLabel')} value={`${formatMoneyNumber(getHourAmount(detail), { language: i18n.language })}h`} />
            <Info label={t('hours.hourlyRateLabel')} value={isPaidHour(detail) ? <MoneyValue value={getHourRate(detail)} /> : t('hours.integratedFree')} />
            <Info className="sm:col-span-2" label={t('hours.calculatedTotal')} value={isPaidHour(detail) ? <MoneyValue value={getHourRate(detail) * getHourAmount(detail)} /> : t('hours.free')} />
          </div>
        )}
      </Modal>
    </div>
  );
};

const Info = ({ label, value, className = '' }) => (
  <div className={`rounded-lg bg-slate-50 p-3 ${className}`}>
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

const Metric = ({ label, value, className = 'text-slate-900' }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
    <p className="text-sm text-slate-600">{label}</p>
    <p className={`mt-2 text-2xl font-bold ${className}`}>{value}</p>
  </section>
);

export default WorkedHoursTab;
